"use client";

import React from "react";
import { 
  Scale, 
  ShieldCheck, 
  FileText, 
  Clock, 
  FileSearch, 
  ArrowRight,
  Gavel,
  Lock,
  Building
} from "lucide-react";

interface LegalHeroProps {
  currency: "INR" | "USD";
  onOpenEvaluationModal: () => void;
  onOpenCnrModal: () => void;
}

export default function LegalHero({
  currency,
  onOpenEvaluationModal,
  onOpenCnrModal,
}: LegalHeroProps) {
  return (
    <section className="relative pt-12 pb-16 md:pt-20 md:pb-24 overflow-hidden border-b border-slate-800 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-slate-950">
      {/* Background Subtle Legal Seal Ambient Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[350px] bg-amber-500/10 blur-[130px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          
          {/* Left Column: Chambers Philosophy & Call to Actions */}
          <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
            {/* Regulatory Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 border border-amber-500/30 text-amber-400 text-xs font-semibold shadow-inner">
              <Gavel className="w-3.5 h-3.5" />
              <span>Chambers of Advocates-on-Record • High Court & Supreme Court of India</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-serif font-bold text-white tracking-tight leading-[1.15]">
              Distinguished Counsel. <br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-400 via-amber-200 to-amber-500">
                Relentless Advocacy.
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-sm sm:text-base text-slate-300 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-normal">
              Specialized trial and appellate representation across High Courts, the Supreme Court of India, NCLT, and international arbitration tribunals. Structured under strict Bar Council ethics with encrypted client matter intelligence.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3.5 pt-2">
              <button
                type="button"
                onClick={onOpenEvaluationModal}
                className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-sm shadow-xl shadow-amber-500/20 transition-all flex items-center justify-center gap-2 group"
              >
                <FileText className="w-4 h-4 text-slate-950" />
                <span>Confidential Case Evaluation</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>

              <button
                type="button"
                onClick={onOpenCnrModal}
                className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-slate-900/90 hover:bg-slate-800/90 border border-slate-700/80 text-slate-200 hover:text-white font-bold text-sm shadow-sm transition-all flex items-center justify-center gap-2"
              >
                <FileSearch className="w-4 h-4 text-amber-400" />
                <span>Track Hearing Status (CNR)</span>
              </button>
            </div>

            {/* Key Trust Pillars */}
            <div className="grid grid-cols-3 gap-3 pt-6 border-t border-slate-800/80 text-left">
              <div className="p-2.5 rounded-xl bg-slate-900/50 border border-slate-800">
                <div className="text-amber-400 font-serif font-bold text-lg">24/7</div>
                <div className="text-[11px] text-slate-400">Emergency Bail & Stay</div>
              </div>
              <div className="p-2.5 rounded-xl bg-slate-900/50 border border-slate-800">
                <div className="text-amber-400 font-serif font-bold text-lg">100%</div>
                <div className="text-[11px] text-slate-400">Privileged & Encrypted</div>
              </div>
              <div className="p-2.5 rounded-xl bg-slate-900/50 border border-slate-800">
                <div className="text-amber-400 font-serif font-bold text-lg">BCI</div>
                <div className="text-[11px] text-slate-400">Rule 36 Compliant</div>
              </div>
            </div>
          </div>

          {/* Right Column: Live Cause List & Matter Intelligence Preview Card */}
          <div className="lg:col-span-5">
            <div className="relative p-6 rounded-2xl bg-gradient-to-b from-slate-900/90 to-slate-950/90 border border-slate-800 shadow-2xl backdrop-blur-xl">
              {/* Card Header */}
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-800">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400">
                    <Scale className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-white">
                      Daily Cause List & Hearing Board
                    </h3>
                    <p className="text-[10px] text-slate-400">High Court of Delhi • Listed Today</p>
                  </div>
                </div>
                <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
                  Active Session
                </span>
              </div>

              {/* Sample High Court Hearing Ticker Item */}
              <div className="space-y-3">
                <div className="p-3.5 rounded-xl bg-slate-850/80 border border-slate-750/70 space-y-2">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="font-mono text-amber-400 font-semibold">ARB/2026/894</span>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-rose-500/10 text-rose-400 border border-rose-500/20 font-bold">
                      ITEM #12 • PASS OVER CALL
                    </span>
                  </div>
                  <h4 className="text-xs font-bold text-white line-clamp-1">
                    AeroTech Dynamics vs. Union Logistics Corp
                  </h4>
                  <div className="text-[11px] text-slate-400 space-y-1">
                    <div className="flex items-center gap-1.5">
                      <Building className="w-3 h-3 text-slate-500 shrink-0" />
                      <span className="line-clamp-1">Court Room 14 • Commercial Division</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Gavel className="w-3 h-3 text-slate-500 shrink-0" />
                      <span className="line-clamp-1">Hon'ble Mr. Justice Rajiv Shakdher</span>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-slate-700/60 flex items-center justify-between text-[10px]">
                    <span className="text-slate-400">Stage: Section 9 Arguments</span>
                    <span className="text-emerald-400 font-semibold">Injunction Extended</span>
                  </div>
                </div>

                {/* Second Upcoming Matter Item */}
                <div className="p-3 rounded-xl bg-slate-900/60 border border-slate-800 text-[11px] flex items-center justify-between">
                  <div>
                    <div className="text-white font-semibold line-clamp-1">State vs. Rajeshwar Goel</div>
                    <div className="text-[10px] text-slate-400">Criminal Bench • Item #4 Tomorrow</div>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
                    Bail Hearing
                  </span>
                </div>
              </div>

              {/* Security Footnote */}
              <div className="mt-4 pt-3 border-t border-slate-800/80 flex items-center justify-between text-[10px] text-slate-500">
                <span className="flex items-center gap-1">
                  <Lock className="w-3 h-3 text-amber-400/80" />
                  Privileged Legal Practice Suite
                </span>
                <span>LexJuris Docket ID: 2026-HC</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
